// router/bootPreempts.js
//
// The decisions behind App.vue's boot-time pre-empts, kept pure so they can be
// tested without mounting the app (supplier-showroom P2-A).
//
// Why pre-empts exist at all: an eager `component:` route whose component
// renders an <f7-page> has its `beforeEnter` SKIPPED on a cold hard-load — F7
// finds the pre-rendered `.page` and takes the DOM-init path instead of
// router.navigate() (f7v9-vue3-playbook Section 5 row 22). App.vue rewrites the
// URL before <f7-view> mounts, mirroring the guard the cold load skips. These
// functions answer "rewrite to where?" — App.vue does the replaceState.
//
// Imports stay light on purpose (the manifest and the Poola paths only): the
// route table pulls in every eager page.
import {
  CLUB_MANIFEST,
  canBrowseSupplierShell,
  clubLandingPath,
  ONBOARDING_ENTRY,
  SUPPLIER_STAGES,
  supplierStageOf,
} from '@/clubs/manifest.js';
import { supplierPoolaTwin } from '@/utils/poolaPaths.js';

/**
 * Seller-shell routes that are eager and param-carrying (a prefix match covers
 * the param and both slash forms) and guarded by requireMembership:
 *  - /issues/ and /issues/:issueId/ — the ItemIssueNeedsDecision push target;
 *  - /posts/:id/ — the `product.unavailable` fan-out target;
 *  - /orders/box/ — the carton pushes (tips-wave P3-A, owner answer W-K);
 *  - /tadbeesa/ — a reload makes it the boot URL (tadbeesa P3-A). The slashless
 *    '/tadbeesa' needs no entry: it matches only the redirect route, which has
 *    no component to pre-render, so the strict route's guard runs.
 */
export const EAGER_MEMBER_REQUIRED_PREFIXES = Object.freeze([
  '/issues/',
  '/posts/',
  '/orders/box/',
  '/tadbeesa/',
]);

/**
 * Seller-shell routes that are eager with an exact path and guarded by
 * requireMembership: «ادعي» (/referral/) and «اقتراحاتي» (/suggestions/), both
 * slash forms. (App.vue also lists them in EAGER_AUTH_REQUIRED_PATHS for the
 * signed-out tier.)
 */
export const EAGER_MEMBER_REQUIRED_PATHS = Object.freeze([
  '/referral/',
  '/referral',
  '/suggestions/',
  '/suggestions',
]);

/**
 * Supplier-shell routes that are eager, guarded by requireSupplier:
 * /suppliers/box/:boxId/ (the order detail; a push / PWA cold-launch deep-link
 * can make it the boot URL) and /suppliers/referral/ (the shop's invite screen;
 * a reload makes it the boot URL). The slashless '/suppliers/referral' needs no
 * entry, as '/tadbeesa' above: it matches only the redirect route, so the
 * strict route's guard runs. The name is the one App.vue and the route
 * comments have always used.
 */
export const EAGER_AUTH_REQUIRED_PREFIXES = Object.freeze(['/suppliers/box/', '/suppliers/referral/']);

/**
 * @typedef {object} BootAuth
 * @property {boolean} isAuthenticated
 * @property {boolean} hasActiveMembership
 * @property {string}  [supplierStage]
 * @property {boolean} [isSupplier]
 * @property {boolean} [isSupplierPreview] a collector previewing one shop (supplier-preview P2-A)
 */

const stageOf = (auth) => ({ supplierStage: auth.supplierStage, isSupplier: auth.isSupplier });

/**
 * The cold-load twin of requireSupplier for the eager supplier routes. Four
 * tiers, in the guard's order:
 *  - signed out              → /login/
 *  - no membership           → onboarding (/join/)
 *  - `interested`            → his waiting screen
 *  - `none` (a seller)       → their landing (/home/)
 * `showroom` and `active` stay (null). A collector previewing a shop
 * (supplier-preview P2-A, L-H) passes the membership tier, as in requireSupplier.
 *
 * @param {string} pathname
 * @param {BootAuth} auth
 * @returns {string|null} the URL to rewrite to, or null to stay.
 */
export function supplierRoutePreempt(pathname, auth) {
  if (!EAGER_AUTH_REQUIRED_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  if (!auth.isAuthenticated) return '/login/';
  if (!auth.hasActiveMembership && !auth.isSupplierPreview) return ONBOARDING_ENTRY;
  if (!canBrowseSupplierShell(stageOf(auth))) return clubLandingPath(stageOf(auth));
  return null;
}

/**
 * Paths a collector previewing a shop may boot on: the supplier shell and
 * everything under /suppliers/ (box detail, Poola), and the gains page, which
 * the showroom banner and the lock sentence open.
 */
const PREVIEW_BOOT_PREFIXES = Object.freeze(['/suppliers/', '/join/gains/']);
/**
 * Pages hidden under the preview because they would show the collector's OWN
 * account under the shop's name: his profile (name and phone), his wallet, and
 * his referrals (the referral endpoints answer his membership, not the shop's).
 * A typed URL or a history entry lands on the shell instead.
 */
const PREVIEW_HIDDEN_PREFIXES = Object.freeze([
  '/suppliers/profile',
  '/suppliers/wallet',
  '/suppliers/referral',
]);

/**
 * The preview wins the boot (supplier-preview P2-A, design Section 4.2, L-H):
 * while a collector previews one shop's supplier app, any boot path outside
 * the preview's paths becomes /suppliers/ — his day (/collector/*) included, so
 * this is also the /collector/ cold-load bounce (requireCollector's preview tier
 * is skipped on a cold hard-load, playbook Section 5 row 22). App.vue runs it
 * before the pure-collector rewrite; `launchLandingPath` and `clubLandingPath`
 * stay as they are. The slashless '/suppliers' and '/join/gains' are rewritten
 * too — harmless, they land on the same page.
 *
 * @param {string} pathname
 * @param {BootAuth} auth
 * @returns {string|null} the URL to rewrite to, or null to stay.
 */
export function supplierPreviewPreempt(pathname, auth) {
  if (!auth.isAuthenticated || !auth.isSupplierPreview) return null;
  if (PREVIEW_HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return CLUB_MANIFEST.suppliers.root;
  if (PREVIEW_BOOT_PREFIXES.some((p) => pathname.startsWith(p))) return null;
  return CLUB_MANIFEST.suppliers.root;
}

/**
 * The supplier tier of the seller-route pre-empts: an authenticated user who is
 * supplier-side (any stage but `none`) never paints a seller page on a cold load.
 *  - a seller member route (the prefixes and paths above) → his landing, like
 *    requireMembership's bounce (the "cold-loaded post page skipped the
 *    supplier guard" fix);
 *  - /poola/ or /poola/last/ → the supplier twin, like the seller Poola
 *    routes' warm guard (the "seller /poola/ shows the seller bar and deposit
 *    banner" fix).
 * The signed-out tiers stay in App.vue (→ /login/). A seller, or any other
 * path, is untouched (null).
 *
 * @param {string} pathname
 * @param {BootAuth} auth
 * @returns {string|null} the URL to rewrite to, or null to stay.
 */
export function sellerRoutePreempt(pathname, auth) {
  if (!auth.isAuthenticated) return null;
  const who = stageOf(auth);
  if (supplierStageOf(who) === SUPPLIER_STAGES.NONE) return null;

  const twin = supplierPoolaTwin(pathname, who);
  if (twin) return twin;

  const isMemberRoute =
    EAGER_MEMBER_REQUIRED_PATHS.includes(pathname) ||
    EAGER_MEMBER_REQUIRED_PREFIXES.some((p) => pathname.startsWith(p));
  return isMemberRoute ? clubLandingPath(who) : null;
}
